/**
 * NetworkPage Component
 * 
 * The main workspace for neural network visualization and interaction.
 * This component orchestrates the entire neural network training, visualization,
 * and testing experience.
 * 
 * Features:
 * - Dataset selection and management
 * - Neural network training controls
 * - Real-time visualization of network architecture
 * - Performance metrics tracking
 * - Model testing interface
 * - Custom dataset support
 * - Model import/export functionality
 * 
 * State Management:
 * - Training state (epochs, loss, accuracy)
 * - Network state (weights, biases, activations)
 * - Dataset configuration
 * - Testing interface state
 * - Metrics history
 * 
 * Component Integration:
 * - CustomDatasetCreator - For creating custom training data
 * - CustomDatasetUploader - For importing external datasets
 * - DatasetSelector - For choosing predefined datasets
 * - NeuronViz - Core visualization component
 * - TrainingControls - Training parameter controls
 * - TestingInterface - Model testing interface
 * 
 * Data Flow:
 * - Dataset selection → Training configuration → Model training
 * - Real-time metrics updates during training
 * - Network state visualization updates
 * - Testing interface for trained models
 * 
 * @component
 */

import DownloadIcon from '@mui/icons-material/Download';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import type { SelectChangeEvent } from '@mui/material';
import { Box, Breadcrumbs, Button, Container, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Divider, Grid, Link, Paper, Tooltip, Typography } from '@mui/material';
import { useRef, useState } from 'react';

// Import components
import { CustomDatasetCreator } from '../components/CustomDatasetCreator';
import { CustomDatasetUploader } from '../components/CustomDatasetUploader';
import { DatasetSelector } from '../components/DatasetSelector';
import { DatasetViewer } from '../components/DatasetViewer';
import { MetricsGraph } from '../components/MetricsGraph';
import { ModelUploader } from '../components/ModelUploader';
import { NeuronViz } from '../components/NeuronViz';
import { TestingInterface } from '../components/TestingInterface';
import { TestModelButton } from '../components/TestModelButton';
import { TrainingControls } from '../components/TrainingControls';
import { TrainingMetrics } from '../components/TrainingMetrics';
import { TrainingStoppedDialog } from '../components/TrainingStoppedDialog';

// Import models and data
import { fitnessData, mapBMI, mapHeartRate, mapStamina } from '../models/fitnessClassification/data';
import { FitnessTrainer } from '../models/fitnessClassification/train';
import { logicGateData, mapGateType } from '../models/logicGates/data';
import { LogicGateTrainer } from '../models/logicGates/train';
import type { ITrainer } from '../models/TrainerInterface';
import { weatherData } from '../models/weatherPrediction/data';
import { WeatherTrainer } from '../models/weatherPrediction/train';

// Import utilities
import { createModelExport } from '../utils/exportUtils';

/**
 * Interface for metric data points used in visualization
 * @interface MetricPoint
 */
interface MetricPoint {
  epoch: number;
  loss: number;
  accuracy: number;
}

/**
 * NetworkPage Component
 * Manages the state and interactions for neural network visualization and training
 */
export default function NetworkPage() {
  // Essential state management for network training and visualization
  const [dataset, setDataset] = useState<string>('');
  const [epochs, setEpochs] = useState<number>(1000);
  const [isTraining, setIsTraining] = useState<boolean>(false);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [iteration, setIteration] = useState<number>(0);
  const [loss, setLoss] = useState<number>(0);
  const [accuracy, setAccuracy] = useState<number>(0);
  const [metricsHistory, setMetricsHistory] = useState<MetricPoint[]>([]);
  
  // Testing interface state
  const [testInputs, setTestInputs] = useState<Record<string, any>>({});
  const [prediction, setPrediction] = useState<number[]>([]);
  const [trainingCompleted, setTrainingCompleted] = useState<boolean>(false);
  const [showTestingInterface, setShowTestingInterface] = useState<boolean>(false);
  
  // Network state management
  const [networkActivations, setNetworkActivations] = useState<number[][]>([]);
  const [networkWeights, setNetworkWeights] = useState<number[][][]>([]);
  const [networkBiases, setNetworkBiases] = useState<number[][]>([]);
  const [networkGradients, setNetworkGradients] = useState<number[][][]>([]);
  
  // Training configuration
  const [learningRate, setLearningRate] = useState(0.01);
  const [trainingSpeed, setTrainingSpeed] = useState(1);
  
  // Custom dataset management
  const [customDataset, setCustomDataset] = useState<any[]>([]);
  const [isUsingCustomDataset, setIsUsingCustomDataset] = useState(false);

  // Reference to the current trainer instance
  const trainerRef = useRef<ITrainer | null>(null);

  const [stopReason, setStopReason] = useState('');
  const [stopDialogOpen, setStopDialogOpen] = useState(false);

  const handleChange = (event: SelectChangeEvent<string>) => {
    setDataset(event.target.value as string);
  };

  const handleEpochChange = (_event: Event, newValue: number | number[]) => {
    setEpochs(newValue as number);
  };

  const handleStartTraining = async () => {
    if (!dataset || isTraining) return;
    
    trainerRef.current = createTrainer(dataset);
    if (!trainerRef.current) return;

    resetTrainingState();
    localStorage.setItem('isTraining', 'true'); // mark training active
    await startTraining();
  };

  const createTrainer = (selectedDataset: string) => {
    switch(selectedDataset) {
      case 'logicGates': 
        return new LogicGateTrainer(isUsingCustomDataset && customDataset.length > 0 ? customDataset : undefined);
      case 'fitnessClassification':
        return new FitnessTrainer(isUsingCustomDataset && customDataset.length > 0 ? customDataset : undefined);
      case 'weatherPrediction':
        return new WeatherTrainer(isUsingCustomDataset && customDataset.length > 0 ? customDataset : undefined);
      default:
        return null;
    }
  };

  const resetTrainingState = () => {
    setIsTraining(true);
    setIsPaused(false);
    setIteration(0);
    setLoss(1);
    setAccuracy(0);
    setMetricsHistory([]);
    setTrainingCompleted(false);
    localStorage.setItem('isTraining', 'false'); // clear training flag on reset
  };

  const startTraining = async () => {
    await trainerRef.current?.train({
      epochs,
      onIteration: handleIteration,
      onComplete: handleComplete,
      onPause: handlePause,
      onStop: handleStop
    });
  };

  const handleIteration = (iter: number, err: number) => {
    setIteration(iter);
    setLoss(err);
    setAccuracy(1 - err);
    setMetricsHistory(prev => [...prev, { epoch: iter, loss: err, accuracy: 1 - err }]);
    
    if (trainerRef.current) {
      const networkState = trainerRef.current.getNetwork().toJSON();
      const networkLayers = [networkState.sizes[0], ...networkState.layers.map((l: { weights: any[] }) => l.weights.length)];
      setNetworkActivations(trainerRef.current.getActivations());
      setNetworkWeights(trainerRef.current.getWeights());
      setNetworkBiases(trainerRef.current.getBiases());
      setNetworkGradients(trainerRef.current.getGradients());
    }
  };

  const handleComplete = () => {
    setIsTraining(false);
    setIsPaused(false);
    setTrainingCompleted(true);
    finishTraining();
  };

  const handlePause = () => {
    setIsPaused(true);
    setIsTraining(false);
  };

  const handleStop = (reason?: string) => {
    if (stopDialogOpen) return; // Avoid multiple dialog triggers
    if (trainerRef.current) {
      trainerRef.current.stop();
      setIsTraining(false);
      setIsPaused(false);
      setTrainingCompleted(false);
    }
    // Use trainerRef.getProgress() to fetch current epoch if available.
    const currentIter = trainerRef.current ? trainerRef.current.getProgress().currentEpoch : iteration;
    setStopReason(`${reason || 'Unknown reason'}. Training stopped at iteration ${currentIter}.`);
    setStopDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setStopDialogOpen(false);
  };

  const handleReset = () => {
    trainerRef.current?.reset();
    setEpochs(1000);
    setLearningRate(0.01);
    setTrainingSpeed(1);
    setIsTraining(false);
    setIsPaused(false);
    setIteration(0);
    setLoss(0);
    setAccuracy(0);
    setMetricsHistory([]);
    setTestInputs({});
    setPrediction([]);
    setTrainingCompleted(false);
    setShowTestingInterface(false);
    setNetworkActivations([]);
    setNetworkWeights([]);
    setNetworkBiases([]);

    if (trainerRef.current && dataset) {
      const defaultArchitecture = getNetworkArchitecture(dataset);
      trainerRef.current.initNetwork(defaultArchitecture);
      setNetworkActivations(trainerRef.current.getActivations());
      setNetworkWeights(trainerRef.current.getWeights());
      setNetworkBiases(trainerRef.current.getBiases());
    }
  };

  const handleTest = () => {
    if (!trainerRef.current) return;
    const input = prepareTestInput();
    const result = trainerRef.current.predict(input);
    setPrediction(result);
  };

  const prepareTestInput = () => {
    switch(dataset) {
      case 'logicGates':
        return [
          testInputs.input1 || 0,
          testInputs.input2 || 0,
          ...mapGateType(testInputs.gateType || 'AND')
        ];
      case 'weatherPrediction':
        // Normalize weather prediction inputs:
        // - Temperature is normalized by dividing by 50 (typical range -10 to 40°C)
        // - Humidity is normalized to 0-1 range by dividing by 100 (0-100%)
        // - Cloud cover is normalized to 0-1 range by dividing by 100 (0-100%)
        return [
          (testInputs.temperature || 0) / 50,
          (testInputs.humidity || 0) / 100,
          (testInputs.cloudCover || 0) / 100
        ];
      case 'fitnessClassification':
        return [
          mapHeartRate(testInputs.heartRate?.toString() || '60-75'),
          mapBMI(testInputs.bmi || 'Normal'),
          mapStamina(testInputs.stamina || 'Medium')
        ];
      default:
        return [];
    }
  };

  const handleResetTest = () => {
    setTestInputs({});
    setPrediction([]);
    setShowTestingInterface(false);
  };

  const handleEnableTesting = () => {
    setShowTestingInterface(true);
  };

  const getNetworkArchitecture = (selectedDataset: string): number[] => {
    switch(selectedDataset) {
      case 'logicGates':
        return [3, 8, 4, 2];
      case 'fitnessClassification':
        return [3, 3, 4, 3];
      case 'weatherPrediction':
        return [3, 3, 4, 1];
      default:
        return [];
    }
  };

  const handleWeightAdjust = (layerIndex: number, fromNeuron: number, toNeuron: number, newWeight: number) => {
    if (trainerRef.current) {
      trainerRef.current.adjustWeight(layerIndex, fromNeuron, toNeuron, newWeight);
      setNetworkWeights(trainerRef.current.getWeights());
      setNetworkActivations(trainerRef.current.getActivations());
      setNetworkBiases(trainerRef.current.getBiases());
      setNetworkGradients(trainerRef.current.getGradients());
    }
  };

  const handleLearningRateChange = (newRate: number) => {
    if (trainerRef.current) {
      trainerRef.current.setLearningRate(newRate);
      setLearningRate(newRate);
    }
  };

  const handleExportNetwork = () => {
    if (trainerRef.current) {
      const networkConfig = trainerRef.current.exportNetwork();
      const blob = new Blob([networkConfig], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `network-config-${dataset}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleImportNetwork = (data: string) => {
    if (trainerRef.current) {
      trainerRef.current.importNetwork(data);
      setNetworkWeights(trainerRef.current.getWeights());
      setNetworkActivations(trainerRef.current.getActivations());
      setNetworkBiases(trainerRef.current.getBiases());
    }
  };

  const handleTrainingSpeedChange = (speed: number) => {
    setTrainingSpeed(speed);
    if (trainerRef.current) {
      const network = trainerRef.current.getNetwork();
      const networkState = network.toJSON();
      networkState.trainOpts = {
        ...networkState.trainOpts,
        iterations: Math.floor(epochs * speed)
      };
      network.fromJSON(networkState);
    }
  };

  const handleArchitectureChange = (newLayers: number[]) => {
    if (isTraining) return;
    
    if (trainerRef.current) {
      handleStop();
      trainerRef.current.reset();
      trainerRef.current.initNetwork(newLayers);
      setNetworkActivations([]);
      setNetworkWeights([]);
      setNetworkBiases([]);
      setMetricsHistory([]);
      setIteration(0);
      setLoss(0);
      setAccuracy(0);
      setTrainingCompleted(false);
      
      if (trainerRef.current) {
        setNetworkActivations(trainerRef.current.getActivations());
        setNetworkWeights(trainerRef.current.getWeights());
        setNetworkBiases(trainerRef.current.getBiases());
      }
    }
  };

  const handleDownloadCustomDataset = () => {
    if (customDataset.length === 0) {
      alert('No custom dataset available to download.');
      return;
    }

    const dataStr = JSON.stringify(customDataset, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `custom-${dataset}-dataset.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportTrainedModel = async () => {
    if (!trainerRef.current) return;
  
    const modelData = JSON.parse(trainerRef.current.exportNetwork());
    const datasetToExport = isUsingCustomDataset ? customDataset : getDefaultDataset();
    const trainingInfo = {
      learningRate,
      epochs,
      architecture: getNetworkArchitecture(dataset),
      datasetType: dataset,
      isCustomDataset: isUsingCustomDataset,
      metrics: trainerRef.current.getPerformanceMetrics()
    };
  
    try {
      const zipBlob = await createModelExport(
        modelData,
        datasetToExport,
        trainingInfo,
        dataset
      );
  
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${dataset}-model-export.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error creating export:', error);
    }
  };

  const handleModelUpload = (modelData: any) => {
    const architecture = modelData.trainingInfo.architecture;
    setDataset(modelData.trainingInfo.datasetType);
    
    if (trainerRef.current) {
      trainerRef.current.importNetwork(JSON.stringify(modelData.model));
    }
    
    setCustomDataset(modelData.dataset);
    setIsUsingCustomDataset(true);
    setLearningRate(modelData.trainingInfo.learningRate);
    setEpochs(modelData.trainingInfo.epochs);
    handleReset();
    setTrainingCompleted(true);
  };

  const handleSaveCustomDataset = (data: any[]) => {
    setCustomDataset(data);
    setIsUsingCustomDataset(true);
    handleReset();
  };

  const handleUseDefaultDataset = () => {
    setIsUsingCustomDataset(false);
    handleReset();
  };

  const handleUseCustomDataset = () => {
    if (customDataset.length === 0) {
      alert('No custom dataset available. Please create one first.');
      return;
    }
    setIsUsingCustomDataset(true);
    handleReset();
  };

  const getDefaultDataset = () => {
    switch(dataset) {
      case 'logicGates':
        return logicGateData.training;
      case 'fitnessClassification':
        return fitnessData.training;
      case 'weatherPrediction':
        return weatherData.training;
      default:
        return [];
    }
  };

  const finishTraining = () => {
    localStorage.setItem('isTraining', 'false');
    setTrainingCompleted(true);
    setStopDialogOpen(true);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          <Link color="inherit" href="/">
            Home
          </Link>
          <Typography color="text.primary">Neural Network</Typography>
        </Breadcrumbs>
      </Box>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Dataset Selection */}
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>Dataset Selection</Typography>
          <Divider sx={{ mb: 2 }} />
          <DatasetSelector dataset={dataset} onChange={handleChange} />
        </Paper>

        {dataset && (
          <>
            {/* Dataset Configuration */}
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>Dataset Configuration</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <DatasetViewer dataset={dataset} title="Default Dataset" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatasetViewer 
                    dataset={dataset} 
                    data={customDataset} 
                    title="Custom Dataset" 
                  />
                </Grid>
              </Grid>

              {/* Dataset Controls */}
              <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  onClick={handleDownloadCustomDataset}
                  disabled={customDataset.length === 0}
                >
                  Download Custom Dataset
                </Button>
                <CustomDatasetUploader
                  dataset={dataset}
                  onUploadDataset={handleSaveCustomDataset}
                />
                <CustomDatasetCreator
                  dataset={dataset}
                  onSaveDataset={handleSaveCustomDataset}
                  minimumRows={10}
                />
              </Box>

              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant={!isUsingCustomDataset ? "contained" : "outlined"}
                    onClick={handleUseDefaultDataset}
                    disabled={isTraining}
                  >
                    Use Default Dataset
                  </Button>
                  <Button
                    variant={isUsingCustomDataset ? "contained" : "outlined"}
                    onClick={handleUseCustomDataset}
                    disabled={customDataset.length === 0 || isTraining}
                  >
                    Use Custom Dataset
                  </Button>
                </Box>
              </Box>
            </Paper>

            {/* Training Controls */}
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>Training Controls</Typography>
              <Divider sx={{ mb: 2 }} />
              <TrainingControls 
                epochs={epochs}
                isTraining={isTraining}
                isPaused={isPaused}
                onEpochChange={handleEpochChange}
                onStart={handleStartTraining}
                onPause={handlePause}
                onContinue={startTraining}
                onStop={handleStop}
                onReset={handleReset}
                currentDataset={isUsingCustomDataset ? 'custom' : 'default'}
              />
            </Paper>

            {/* Training Progress */}
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>Training Progress</Typography>
              <Divider sx={{ mb: 2 }} />
              <TrainingMetrics 
                iteration={iteration}
                epochs={epochs}
                loss={loss}
                accuracy={accuracy}
                isTraining={isTraining}
              />
              <MetricsGraph data={metricsHistory} />
            </Paper>

            {/* Network Visualization */}
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>Network Visualization</Typography>
              <Divider sx={{ mb: 2 }} />
              <NeuronViz 
                layers={getNetworkArchitecture(dataset)}
                activations={networkActivations}
                weights={networkWeights}
                biases={networkBiases}
                dataset={dataset}
                isTraining={isTraining}
                onWeightAdjust={handleWeightAdjust}
                learningRate={learningRate}
                onLearningRateChange={handleLearningRateChange}
                onExportNetwork={handleExportNetwork}
                onImportNetwork={handleImportNetwork}
                performanceMetrics={trainerRef.current?.getPerformanceMetrics()}
                trainingSpeed={trainingSpeed}
                onTrainingSpeedChange={handleTrainingSpeedChange}
                onArchitectureChange={handleArchitectureChange}
              />
            </Paper>

            {/* Testing Interface */}
            {!isTraining && iteration > 0 && (
              <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom>Model Testing</Typography>
                <Divider sx={{ mb: 2 }} />
                <TestModelButton 
                  trainingCompleted={trainingCompleted}
                  isTestingEnabled={showTestingInterface}
                  onEnableTesting={handleEnableTesting}
                />
                
                {showTestingInterface && (
                  <Box sx={{ mt: 2 }}>
                    <TestingInterface 
                      dataset={dataset}
                      testInputs={testInputs}
                      prediction={prediction}
                      onTestInputChange={(key, value) => 
                        setTestInputs(prev => ({ ...prev, [key]: value }))}
                      onTest={handleTest}
                      onReset={handleResetTest}
                    />
                  </Box>
                )}
              </Paper>
            )}

            {/* Export & Import */}
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>Export & Import</Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Tooltip title="Download the trained model configuration and dataset">
                  <span>
                    <Button
                      variant="contained"
                      onClick={handleExportTrainedModel}
                      startIcon={<DownloadIcon />}
                      disabled={!trainingCompleted}
                    >
                      Export Trained Model & Dataset
                    </Button>
                  </span>
                </Tooltip>
                <ModelUploader onModelUpload={handleModelUpload} />
              </Box>
            </Paper>
          </>
        )}
      </Box>
      <TrainingStoppedDialog open={stopDialogOpen} reason={stopReason} onClose={handleCloseDialog} />
      {stopDialogOpen && (
        <Dialog open={stopDialogOpen} onClose={() => setStopDialogOpen(false)}>
          <DialogTitle>Training Completed</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Training is finished. You can now test the model.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStopDialogOpen(false)}>OK</Button>
          </DialogActions>
        </Dialog>
      )}
    </Container>
  );
}
